import React from 'react';
import axios, { AxiosRequestConfig, Canceler } from 'axios';

import APIConfig, { DiversityClassificationFilter, DiscoveryRequestFilterOptions } from 'config/api';
import { CompanyType, COMPANY_KEY } from 'constants/company';
import { EVENT } from 'constants/event';
import {
  CountryType,
  FILTER_TREE_TYPE,
  FilterTreeType,
  INVITED_EVENT_COUNT_MAX,
  SEARCH_FILTER_NAME,
  supplierActivityFilterInvitedEventParentTreeNode,
  supplierActivityFilterInvitedPortalParentTreeNode,
  SearchFilterNameValueType,
  sourceFilterRootTreeNode,
  SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID
} from 'constants/filter';
import {
  isGlobalSupplierListCategoryItem,
  LIST_CATEGORY_ID,
  ListCategoryIdType,
  ListCategoryMap
} from 'constants/list';
import { PRODUCT, SCREEN_DESCRIPTION } from 'constants/product';
import { ItemSchemaNameType, ITEM_SCHEMA_NAME } from 'constants/schema';
import { SEARCH_MODE, SearchModeType, SEARCH_RESULT_VIEW_MODE, SearchResultViewModeType } from 'constants/search';
import { useEventBus } from 'hooks/event/useEventBus';
import { useQueryParameters } from 'hooks/url/useQueryParameters';
import { DiversityTypeMap, transformDiversityTypeMapToTreeNodes } from 'util/diversity';
import { createRequestErrorMessage } from 'util/error';
import {
  addMatchesDiscoveryFiltersToEipCompanyList,
  addMatchesDiscoveryFiltersToFrequentContactList,
  AppliedFilterChipType,
  generateFilterTreeNodesWithCheckedNodes,
  generateFilterTreeNodesWithOpenNodes,
  getAppliedFilterChipForTreeNode,
  getAppliedFilterChipsWithUpdatedChildrenStatus,
  getCheckedFilterIdsAfterNodeChecked,
  getCheckedFilterIdsAfterNodeUnchecked,
  getFilteredSearchResults,
  getFilterNodeById,
  getFilterParentNode,
  getFilterParentNodeIds,
  getSupplierActivityCountFiltersFromRange,
  generateDiverstiyClassificationFilters,
  getRolledUpAppliedFilters,
  getSummaryTextForSupplierActivityCountFilterRange,
  SearchResultFilterType,
  searchResultInvitedEventCountFilter,
  searchResultInvitedPortalCountFilter,
  searchResultParentCompanyPersonFilter,
  SearchResultParentCompanyPersonFilterType,
  treeTypeToSearchFilter
} from 'util/filter';
import { noop } from 'util/function';
import { convertStringListToIntegerList } from 'util/number';
import {
  getCompanyCountFromSearchResults,
  getSearchResultsSortedAlphabetically,
  getSearchResultsSortedByRelevancyScore,
  getUniqueCompanyKeysFromSearchResults,
  getUniquePersonIdsFromSearchResults,
  getUserCountFromSearchResults,
  mergeTreeNodesAndPowerScoreResults
} from 'util/search';
import { getAllSupplierIdsForSupplierTree } from 'util/supplier';
import { transformBidEventItemResultsToListNameItems } from 'util/transform/bidEvent';
import {
  getCountryIdsFromCountryStateList,
  getStateIdsFromCountryStateList,
  transformCountryListToTreeNodes
} from 'util/transform/country';
import { FilterTreeNode, transformRawFilterNodeCollectionToTreeNodes } from 'util/transform/filter';
import { transformPowerScoreContactListToTreeNodes } from 'util/transform/powerScoreContactList';
import { transformSearchResultToTreeNodes } from 'util/transform/search';
import {
  getCompanyKeysFromGroupedSupplierList,
  transformSupplierListMembersToSearchResult,
  transformSupplierListResultsToListNameItems
} from 'util/transform/supplierList';
import { TransformedFilterTreeNode } from 'components/tree/FilterTree';

//
// --- Types ---

/**
 * DiscoverySearchFilterOptionsType is very similar to the list of properties we send to discovery API
 * requests, but there are some differences. This type is for describing how filters are  stored in search
 * context state, but where possible, uses the types defined for API requests as a base.
 */
interface DiscoverySearchFilterOptionsType
  extends Pick<
    DiscoveryRequestFilterOptions,
    | 'diversityClassificationFilters'
    | 'diversityFilters'
    | 'invitedEventCountFilters'
    | 'invitedPortalCountFilters'
    | 'segmentationFilters'
    | 'sourceFilters'
  > {
  /** ui uses combined country/state filters list; api uses separate lits; split by util functions in this file; both have same type */
  countryOrStateFilters?: DiscoveryRequestFilterOptions['countryFilters'];
  /* ui uses string id values; api uses number values; converted by util function in ths file */
  itemFilterIds?: ReadonlyArray<string>;
  /* uses same type, but property value name is different */
  qualificationFilterIds?: DiscoveryRequestFilterOptions['qualificationFilters'];
}

type FilterTreeDataMap = Record<FilterTreeType, ReadonlyArray<TransformedFilterTreeNode>>;

interface InitializeStateOptions {
  isDiscovery?: boolean;
  searchTerm?: string;
}

export type ListNameItemType = {
  listCategoryId: ListCategoryIdType;
  key: string;
  name: string;
};

type LoadFilterDataReturnValue = {
  countryList: ReadonlyArray<CountryType>;
  diversityTypeMap: DiversityTypeMap;
  hasViewSupplierPermission: boolean;
  rawFilterRootNode: FilterTreeNode;
  supplierIntelSchemaName: ItemSchemaNameType;
};

type SearchContextState = {
  /** list of applied chips in the filter panel */
  appliedFilterChips: ReadonlyArray<AppliedFilterChipType>;
  /** true if request to load lists for a list category is running */
  areListNamesLoading: boolean;
  /** true if search request is running */
  areSearchResultsLoading: boolean;
  /** map of lists of filter ids that are checked in different trees of filter panel*/
  checkedFilterIdsMap: Record<FilterTreeType, ReadonlyArray<string>>;
  /** names for lists in the currently selected list category */
  currentListNames: ReadonlyArray<ListNameItemType>;
  /** buyer company key used to generate powerscore values for `currentSearchResults` */
  currentSearchBuyerCompanyKey?: number;
  /** company count for current search results */
  currentSearchCompanyCount: number;
  /** unique company keys for companies listed in currentSearchResults */
  currentSearchResultCompanyKeys: ReadonlyArray<number>;
  /** results for last run search */
  currentSearchResults: ReadonlyArray<any>;
  /** supplier intelligence schema being used by the company this user belongs to */
  currentSupplierIntelSchemaName: ItemSchemaNameType | '';
  /** user count for current search results */
  currentSearchUserCount: number;
  /** list of supplier ids being excluded by the parentCompanyPersonFilter */
  excludedParentCompanyPersonFilterSupplierIds: ReadonlyArray<string>;
  /** current filter data trees in state */
  filterTreeDataMap: FilterTreeDataMap;
  /** default transformed version of each filter tree */
  filterTreeDataMapDefault: FilterTreeDataMap;
  /** true if a search has been run, false otherwise */
  hasSearchBeenRun: boolean;
  /** end of invited event count filter range */
  invitedEventCountRangeEnd: number;
  /** start of invited event count filter range */
  invitedEventCountRangeStart: number;
  /** current invited event count state data tree; this is not displayed, but needed for applied filter calculations */
  invitedEventCountTreeData: ReadonlyArray<TransformedFilterTreeNode>;
  /** end of invited event count filter range */
  invitedPortalCountRangeEnd: number;
  /** start of invited event count filter range */
  invitedPortalCountRangeStart: number;
  /** current invited event count state data tree; this is not displayed, but needed for applied filter calculations */
  invitedPortalCountTreeData: ReadonlyArray<TransformedFilterTreeNode>;
  /** true if user has alphabetical order selected, showing alphabetically sorted suppliers */
  isAlphabeticalOrderEnabled: boolean;
  /** true by default, false if the user chooses to search by person instead */
  isCompanySearch: boolean;
  /** true if requests to load filter data is running */
  isFilterTreeDataLoading: boolean;
  /** list of filter ids for nodes that are currently open in the filter panel */
  openFilterIdsMap: Record<FilterTreeType, ReadonlyArray<string>>;
  /** array of active filters; elements are objects that have a filter name and filter data properties */
  searchFilters: ReadonlyArray<SearchResultFilterType>;
  /** search mode the app is running in */
  searchMode: SearchModeType;
  /** search phrase used to generate `currentSearchResults` */
  searchPhraseForLastSearch?: string;
  /** current phrase in the search bar **/
  searchPhraseInputValue: string;
  /** id for currently selected list category */
  selectedListCategoryId: ListCategoryIdType;
  /** list key string for last selected list */
  selectedListKey?: string;
  /** one of 'tree', 'hybrid', 'list'; view mode of the search results */
  viewMode: SearchResultViewModeType;
};

interface RemoveCheckedFilterOptions {
  /** should all checked filter ids be removed, or only individual one provided */
  removeAll?: boolean;
}

interface SearchOptionsType extends Omit<DiscoverySearchFilterOptionsType, 'diversityClassificationFilters'> {
  buyerCompanyKey?: number;
  excludePeople?: boolean;
  isCompanySearch?: boolean;
  supplierIntelSchemaName?: ItemSchemaNameType;
  viewMode?: SearchResultViewModeType;
  diversityClassificationFilters?: ReadonlyArray<DiversityClassificationFilter>;
  useNewSupplierSearch?: boolean;
}

type SearchContextValueActionType = {
  addCheckedFilter: (
    buyerCompanyKey: number,
    filterTreeType: FilterTreeType,
    filterId: string,
    useNewSupplierSearch: boolean
  ) => void;
  addSearchFilter: (searchFilter: SearchResultFilterType) => void;
  addSupplierParentCompanyFilterExclusion: (supplierId: string) => void;
  removeCheckedFilter: (
    buyerCompanyKey: number,
    filterTreeType: FilterTreeType,
    filterId: string,
    useNewSupplierSearch: boolean,
    opts?: RemoveCheckedFilterOptions
  ) => void;
  removeSearchFilter: (searchFilterName: SearchFilterNameValueType | ReadonlyArray<SearchFilterNameValueType>) => void;
  resetSearchFilters: () => void;
  resetSearchResults: () => void;
  runAllCompanySearch: (
    useNewSupplierSearch: boolean,
    page?: number,
    pageSize?: number
  ) => Promise<{ resultCount: number; searchResults: ReadonlyArray<any> }>;
  runBidEventItemListRequest: (offeringKey: number) => void;
  runListMemberRequest: (listName: ListNameItemType, buyerCompanyKey: number) => void;
  runSearch: (searchPhrase: string, searchOptions?: SearchOptionsType) => void;
  runSupplierListRequest: (listCategoryId: ListCategoryIdType) => void;
  setSearchResultsCompanyCount: (companyCount: number) => void;
  setCompanySearchEnabled: (isCompanySearch: boolean) => void;
  setSearchResultsLoading: (isLoading: boolean) => void;
  updateFilterOpenState: (filterTreeType: FilterTreeType, filterId: string, isOpen: boolean) => void;
  updateInvitedEventCountFilter: (
    buyerCompanyKey: number,
    startingCount: number,
    endingCount: number,
    useNewSupplierSearch: boolean
  ) => void;
  updateInvitedPortalCountFilter: (
    buyerCompanyKey: number,
    startingCount: number,
    endingCount: number,
    useNewSupplierSearch: boolean
  ) => void;
  updateListCategory: (listCategoryId: ListCategoryIdType) => void;
  updateSearchPhraseInputValue: (searchPhraseInputValue: string) => void;
  updateSearchMode: (searchMode: SearchModeType, phrase?: string) => void;
  updateSearchResultsAlphabeticalOrderEnabled: (isAlphabeticalOrderEnabled: boolean) => void;
  updateSearchResultsViewMode: (viewMode: SearchResultViewModeType) => void;
  getSearchContextState: any;
};

type SearchContextValue = [searchState: SearchContextState, searchAction: SearchContextValueActionType];

// reducer state and actions
interface Action {
  type: string;
}

//

interface AppliedFilterChipsUpdateAction extends Action {
  type: typeof APPLIED_FILTER_CHIPS_UPDATE;
  appliedFilterChips: ReadonlyArray<AppliedFilterChipType>;
  checkedFilterIds: ReadonlyArray<string>;
}

interface CheckedCountryOrStateFilterUpdateAction extends Action {
  type: typeof CHECKED_COUNTRY_OR_STATE_FILTER_UPDATE;
  checkedCountryOrStateFilterIds: ReadonlyArray<string>;
}

interface CheckedFilterUpdateAction extends Action {
  type: typeof CHECKED_FILTER_UPDATE;
  checkedFilterIds: ReadonlyArray<string>;
  filterTreeType: FilterTreeType;
}

interface CheckedItemFilterUpdateAction extends Action {
  type: typeof CHECKED_ITEM_FILTER_UPDATE;
  checkedItemFilterIds: ReadonlyArray<string>;
}

interface FiltersLoadingAction extends Action {
  type: typeof FILTERS_LOADING;
  isLoading: boolean;
}

interface FilterTreeDataSetLoadingAction extends Action {
  type: typeof FILTER_TREE_DATA_SET_LOADING;
  isLoading: boolean;
}

interface FilterTreeDataUpdateAction extends Action {
  type: typeof FILTER_TREE_DATA_UPDATE;
  filterTreeDataMap: Partial<FilterTreeDataMap>;
}

interface InvitedEventFilterRangeUpdateAction extends Action {
  type: typeof INVITED_EVENT_FILTER_RANGE_UPDATE;
  end: number;
  start: number;
}

interface InvitedPortalFilterRangeUpdateAction extends Action {
  type: typeof INVITED_PORTAL_FILTER_RANGE_UPDATE;
  end: number;
  start: number;
}

interface ListCategoryChangeAction extends Action {
  type: typeof LIST_CATEGORY_CHANGE;
  listCategoryId: ListCategoryIdType;
}

interface ListNameResultResetAction extends Action {
  type: typeof LIST_NAME_RESULT_RESET;
}

interface ListNameResultSetLoadingAction extends Action {
  type: typeof LIST_NAME_RESULT_SET_LOADING;
  isLoading: boolean;
}

interface ListNameResultUpdateAction extends Action {
  type: typeof LIST_NAME_RESULT_UPDATE;
  listNames: ReadonlyArray<ListNameItemType>;
}

interface ListSetSelectedAction extends Action {
  type: typeof LIST_SET_SELECTED;
  listKey: string;
}

interface OpenFilterUpdateAction extends Action {
  type: typeof OPEN_FILTER_UPDATE;
  filterId: string;
  filterTreeType: FilterTreeType;
  isOpen: boolean;
}

interface SearchFilterAddAction extends Action {
  type: typeof SEARCH_FILTER_ADD;
  searchFilter: SearchResultFilterType;
}

interface SearchFilterRemoveAction extends Action {
  type: typeof SEARCH_FILTER_REMOVE;
  searchFilterName: SearchFilterNameValueType | ReadonlyArray<SearchFilterNameValueType>;
}

interface SearchFilterResetAction extends Action {
  type: typeof SEARCH_FILTER_RESET;
}

interface SearchHasBeenRunAction extends Action {
  type: typeof SEARCH_HAS_BEEN_RUN;
}

interface SearchModeChangeAction extends Action {
  type: typeof SEARCH_MODE_CHANGE;
  searchMode: SearchModeType;
}

interface SearchPhraseInputValueUpdateAction extends Action {
  type: typeof SEARCH_PHRASE_INPUT_VALUE_UPDATE;
  searchPhraseInputValue: string;
}

interface SearchResultsResetAction extends Action {
  type: typeof SEARCH_RESULTS_RESET;
}

interface SearchResultsSetAlphabeticalOrderEnabledAction extends Action {
  type: typeof SEARCH_RESULTS_SET_ALPHABETICAL_ORDER_ENABLED;
  isEnabled: boolean;
}

interface SearchResultsSetLoadingAction extends Action {
  type: typeof SEARCH_RESULTS_SET_LOADING;
  isLoading: boolean;
}

interface SearchResultsSetSearchParamsAction extends Action {
  type: typeof SEARCH_RESULTS_SET_SEARCH_PARAMS;
  buyerCompanyKey?: number;
  searchPhrase: string;
}

interface SearchResultsSetCompanyCountAction extends Action {
  type: typeof SEARCH_RESULTS_SET_COMPANY_COUNT;
  companyCount: number;
}

interface SearchResultsSetCompanySearchEnabledAction extends Action {
  type: typeof SEARCH_RESULTS_SET_COMPANY_SEARCH_ENABLED;
  isCompanySearch: boolean;
}

interface SearchResultsSetViewModeAction extends Action {
  type: typeof SEARCH_RESULTS_SET_VIEW_MODE;
  viewMode: SearchResultViewModeType;
}

interface SearchResultsUpdateAction extends Action {
  type: typeof SEARCH_RESULTS_UPDATE;
  searchResults: ReadonlyArray<any>;
}

interface SupplierSchemaNameSetAction extends Action {
  type: typeof SUPPLIER_SCHEMA_NAME_SET;
  supplierIntelSchemaName: ItemSchemaNameType;
}

type SearchContextAction =
  | AppliedFilterChipsUpdateAction
  | CheckedCountryOrStateFilterUpdateAction
  | CheckedFilterUpdateAction
  | CheckedItemFilterUpdateAction
  | FiltersLoadingAction
  | FilterTreeDataSetLoadingAction
  | FilterTreeDataUpdateAction
  | InvitedEventFilterRangeUpdateAction
  | InvitedPortalFilterRangeUpdateAction
  | ListCategoryChangeAction
  | ListNameResultResetAction
  | ListNameResultSetLoadingAction
  | ListNameResultUpdateAction
  | ListSetSelectedAction
  | OpenFilterUpdateAction
  | SearchFilterAddAction
  | SearchFilterRemoveAction
  | SearchFilterResetAction
  | SearchHasBeenRunAction
  | SearchModeChangeAction
  | SearchPhraseInputValueUpdateAction
  | SearchResultsResetAction
  | SearchResultsSetAlphabeticalOrderEnabledAction
  | SearchResultsSetCompanyCountAction
  | SearchResultsSetCompanySearchEnabledAction
  | SearchResultsSetLoadingAction
  | SearchResultsSetSearchParamsAction
  | SearchResultsSetViewModeAction
  | SearchResultsUpdateAction
  | SupplierSchemaNameSetAction;

//
// --- Default Context Object ---

const CancelToken = axios.CancelToken;
let cancel: Canceler;

const DEFAULT_SEARCH_DATA: SearchContextState = {
  appliedFilterChips: [],
  areListNamesLoading: false,
  areSearchResultsLoading: false,
  checkedFilterIdsMap: Object.values(FILTER_TREE_TYPE).reduce(
    (map, currentFilterTreeType) => ({ ...map, [currentFilterTreeType]: [] }),
    {} as Record<FilterTreeType, readonly string[]>
  ),
  currentListNames: [],
  currentSearchBuyerCompanyKey: undefined,
  currentSearchCompanyCount: 0,
  currentSearchResultCompanyKeys: [],
  currentSearchResults: [],
  currentSupplierIntelSchemaName: '',
  currentSearchUserCount: 0,
  excludedParentCompanyPersonFilterSupplierIds: [],
  // map each filter tree type to an empty array (except invited event count which is static)
  filterTreeDataMap: {
    [FILTER_TREE_TYPE.CONTACT]: [],
    [FILTER_TREE_TYPE.COUNTRY_OR_STATE]: [],
    [FILTER_TREE_TYPE.DIVERSE]: [],
    [FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION]: [],
    [FILTER_TREE_TYPE.INVITED_EVENT_COUNT]: [supplierActivityFilterInvitedEventParentTreeNode],
    [FILTER_TREE_TYPE.INVITED_PORTAL_COUNT]: [supplierActivityFilterInvitedPortalParentTreeNode],
    [FILTER_TREE_TYPE.ITEM]: [],
    [FILTER_TREE_TYPE.QUALIFICATION]: [],
    [FILTER_TREE_TYPE.SEGMENTATION]: [],
    [FILTER_TREE_TYPE.SOURCE]: [sourceFilterRootTreeNode]
  },
  // map each filter tree type to an empty array (except invited event count which is static)
  filterTreeDataMapDefault: {
    [FILTER_TREE_TYPE.CONTACT]: [],
    [FILTER_TREE_TYPE.COUNTRY_OR_STATE]: [],
    [FILTER_TREE_TYPE.DIVERSE]: [],
    [FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION]: [],
    [FILTER_TREE_TYPE.INVITED_EVENT_COUNT]: [supplierActivityFilterInvitedEventParentTreeNode],
    [FILTER_TREE_TYPE.INVITED_PORTAL_COUNT]: [supplierActivityFilterInvitedPortalParentTreeNode],
    [FILTER_TREE_TYPE.ITEM]: [],
    [FILTER_TREE_TYPE.QUALIFICATION]: [],
    [FILTER_TREE_TYPE.SEGMENTATION]: [],
    [FILTER_TREE_TYPE.SOURCE]: [sourceFilterRootTreeNode]
  },
  hasSearchBeenRun: false,
  invitedEventCountRangeEnd: INVITED_EVENT_COUNT_MAX,
  invitedEventCountRangeStart: 0,
  invitedEventCountTreeData: [supplierActivityFilterInvitedEventParentTreeNode],
  invitedPortalCountRangeEnd: INVITED_EVENT_COUNT_MAX,
  invitedPortalCountRangeStart: 0,
  invitedPortalCountTreeData: [supplierActivityFilterInvitedPortalParentTreeNode],
  isAlphabeticalOrderEnabled: false,
  isCompanySearch: true,
  isFilterTreeDataLoading: false,
  // map each filter tree type to an empty array (except invited event count which always has a child node open)
  openFilterIdsMap: {
    [FILTER_TREE_TYPE.CONTACT]: [],
    [FILTER_TREE_TYPE.COUNTRY_OR_STATE]: [],
    [FILTER_TREE_TYPE.DIVERSE]: [],
    [FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION]: [],
    [FILTER_TREE_TYPE.INVITED_EVENT_COUNT]: [SEARCH_FILTER_NAME.INVITED_EVENT_COUNT],
    [FILTER_TREE_TYPE.INVITED_PORTAL_COUNT]: [SEARCH_FILTER_NAME.INVITED_PORTAL_COUNT],
    [FILTER_TREE_TYPE.ITEM]: [],
    [FILTER_TREE_TYPE.QUALIFICATION]: [],
    [FILTER_TREE_TYPE.SEGMENTATION]: [],
    [FILTER_TREE_TYPE.SOURCE]: []
  },
  searchFilters: [],
  searchMode: SEARCH_MODE.SUPPLIER_SEARCH,
  searchPhraseForLastSearch: undefined,
  searchPhraseInputValue: '',
  selectedListCategoryId: LIST_CATEGORY_ID.COMPANY_LISTS,
  selectedListKey: undefined,
  viewMode: SEARCH_RESULT_VIEW_MODE.HYBRID
};

//
// --- Search Context ---

/**
 * Context for storing data and actions specific to the current search
 */
const SearchContext = React.createContext<SearchContextValue>([
  DEFAULT_SEARCH_DATA,
  {
    addCheckedFilter: noop,
    addSearchFilter: noop,
    addSupplierParentCompanyFilterExclusion: noop,
    removeCheckedFilter: noop,
    removeSearchFilter: noop,
    resetSearchFilters: noop,
    resetSearchResults: noop,
    runAllCompanySearch: () => Promise.resolve({ resultCount: 0, searchResults: [], useNewSupplierSearch: false }),
    runBidEventItemListRequest: noop,
    runListMemberRequest: noop,
    runSearch: noop,
    runSupplierListRequest: noop,
    setSearchResultsCompanyCount: noop,
    setCompanySearchEnabled: noop,
    setSearchResultsLoading: noop,
    updateFilterOpenState: noop,
    updateInvitedEventCountFilter: noop,
    updateInvitedPortalCountFilter: noop,
    updateListCategory: noop,
    updateSearchMode: noop,
    updateSearchPhraseInputValue: noop,
    updateSearchResultsAlphabeticalOrderEnabled: noop,
    updateSearchResultsViewMode: noop,
    getSearchContextState: () => {}
  }
]);

//
// --- Search Context Reducer ---

// reducer actions
const APPLIED_FILTER_CHIPS_UPDATE = 'applied-filter-chips-update';
const CHECKED_COUNTRY_OR_STATE_FILTER_UPDATE = 'checked-country-or-state-filter-update';
const CHECKED_FILTER_UPDATE = 'checked-filter-update';
const CHECKED_ITEM_FILTER_UPDATE = 'checked-item-filter-update';
const FILTER_TREE_DATA_SET_LOADING = 'filter-tree-data-set-loading';
const FILTER_TREE_DATA_UPDATE = 'filter-tree-data-update';
const FILTERS_LOADING = 'filters-loading';
const INVITED_EVENT_FILTER_RANGE_UPDATE = 'invited-event-filter-range-update';
const INVITED_PORTAL_FILTER_RANGE_UPDATE = 'invited-portal-filter-range-update';
const LIST_CATEGORY_CHANGE = 'list-category-change';
const LIST_NAME_RESULT_RESET = 'list-name-result-reset';
const LIST_NAME_RESULT_SET_LOADING = 'list-category-list-names-set-loading';
const LIST_NAME_RESULT_UPDATE = 'list-name-result-update';
const LIST_SET_SELECTED = 'list-set-selected';
const OPEN_FILTER_UPDATE = 'open-filter-update';
const SEARCH_FILTER_ADD = 'search-filter-add';
const SEARCH_FILTER_REMOVE = 'search-filter-remove';
const SEARCH_FILTER_RESET = 'search-filter-reset';
const SEARCH_HAS_BEEN_RUN = 'search-has-been-run';
const SEARCH_MODE_CHANGE = 'search-mode-change';
const SEARCH_PHRASE_INPUT_VALUE_UPDATE = 'search-phrase-input-value-update';
const SEARCH_RESULTS_RESET = 'search-results-reset';
const SEARCH_RESULTS_SET_ALPHABETICAL_ORDER_ENABLED = 'search-results-set-alphabetical-order-enabled';
const SEARCH_RESULTS_SET_COMPANY_COUNT = 'search-results-set-company-count';
const SEARCH_RESULTS_SET_COMPANY_SEARCH_ENABLED = 'search-results-set-company-search-enabled';
const SEARCH_RESULTS_SET_LOADING = 'search-results-set-loading';
const SEARCH_RESULTS_SET_SEARCH_PARAMS = 'search-results-set-search-params';
const SEARCH_RESULTS_SET_VIEW_MODE = 'search-results-set-view-mode';
const SEARCH_RESULTS_UPDATE = 'search-results-update';
const SUPPLIER_SCHEMA_NAME_SET = 'supplier-schema-name-set';

// default state
const defaultState = {
  ...DEFAULT_SEARCH_DATA
};

const initializeState =
  ({ isDiscovery = false, searchTerm }: InitializeStateOptions = {}) =>
  (defaultState: SearchContextState) => {
    return {
      ...defaultState,
      // all company search starts out loading so set this to prevent no search results from showing up for a split second
      areSearchResultsLoading: isDiscovery,
      searchPhraseForLastSearch: searchTerm || defaultState.searchPhraseForLastSearch,
      searchPhraseInputValue: searchTerm || defaultState.searchPhraseInputValue,
      viewMode: isDiscovery ? SEARCH_RESULT_VIEW_MODE.LIST : SEARCH_RESULT_VIEW_MODE.HYBRID
    };
  };

// reducer
function searchContextReducer(state: SearchContextState, action: SearchContextAction): SearchContextState {
  switch (action.type) {
    case APPLIED_FILTER_CHIPS_UPDATE:
      const combinedTreeData = Object.values(state.filterTreeDataMap).reduce((acc, treeData) => [...acc, ...treeData]);

      return {
        ...state,
        appliedFilterChips: getAppliedFilterChipsWithUpdatedChildrenStatus(
          combinedTreeData,
          action.appliedFilterChips,
          action.checkedFilterIds
        )
      };

    case CHECKED_FILTER_UPDATE:
      const newFilterTreeDataMap = generateFilterTreeNodesWithCheckedNodes(
        state.filterTreeDataMap[action.filterTreeType],
        action.checkedFilterIds
      );

      return {
        ...state,
        checkedFilterIdsMap: { ...state.checkedFilterIdsMap, [action.filterTreeType]: action.checkedFilterIds },
        filterTreeDataMap: { ...state.filterTreeDataMap, [action.filterTreeType]: newFilterTreeDataMap }
      };

    case FILTER_TREE_DATA_SET_LOADING:
      return { ...state, isFilterTreeDataLoading: action.isLoading };

    case FILTER_TREE_DATA_UPDATE:
      return {
        ...state,
        filterTreeDataMap: { ...state.filterTreeDataMap, ...action.filterTreeDataMap },
        filterTreeDataMapDefault: { ...state.filterTreeDataMapDefault, ...action.filterTreeDataMap }
      };

    case INVITED_EVENT_FILTER_RANGE_UPDATE:
      return {
        ...state,
        invitedEventCountRangeEnd: action.end,
        invitedEventCountRangeStart: action.start
      };

    case INVITED_PORTAL_FILTER_RANGE_UPDATE:
      return {
        ...state,
        invitedPortalCountRangeEnd: action.end,
        invitedPortalCountRangeStart: action.start
      };

    case LIST_CATEGORY_CHANGE:
      return { ...state, selectedListCategoryId: action.listCategoryId };

    case LIST_NAME_RESULT_RESET:
      return { ...state, currentListNames: defaultState.currentListNames };
    case LIST_NAME_RESULT_SET_LOADING:
      return { ...state, areListNamesLoading: action.isLoading };
    case LIST_NAME_RESULT_UPDATE:
      const sortedListNames = Array.isArray(action.listNames)
        ? action.listNames.sort((itemA: ListNameItemType, itemB: ListNameItemType) =>
            itemA.name.localeCompare(itemB.name)
          )
        : [];
      return { ...state, currentListNames: sortedListNames };
    case LIST_SET_SELECTED:
      return { ...state, selectedListKey: action.listKey };

    case OPEN_FILTER_UPDATE:
      const newOpenFilterIdsMap = action.isOpen
        ? [...state.openFilterIdsMap[action.filterTreeType], action.filterId]
        : state.openFilterIdsMap[action.filterTreeType].filter(openFilterId => openFilterId !== action.filterId);

      return {
        ...state,
        openFilterIdsMap: {
          ...state.openFilterIdsMap,
          [action.filterTreeType]: newOpenFilterIdsMap
        },
        filterTreeDataMap: {
          ...state.filterTreeDataMap,
          [action.filterTreeType]: generateFilterTreeNodesWithOpenNodes(
            state.filterTreeDataMap[action.filterTreeType],
            newOpenFilterIdsMap
          )
        }
      };

    case SEARCH_FILTER_ADD:
      const filterIndex = state.searchFilters.findIndex(
        (filter: SearchResultFilterType) => filter.name === action.searchFilter.name
      );

      const newSearchFilters =
        filterIndex === -1
          ? [...state.searchFilters, action.searchFilter]
          : [
              ...state.searchFilters.slice(0, filterIndex),
              action.searchFilter,
              ...state.searchFilters.slice(filterIndex + 1)
            ];

      const parentCompanyPersonFilter = newSearchFilters.find(
        filter => filter.name === SEARCH_FILTER_NAME.PARENT_COMPANY_PERSON
      ) as SearchResultParentCompanyPersonFilterType;

      return {
        ...state,
        excludedParentCompanyPersonFilterSupplierIds:
          parentCompanyPersonFilter && parentCompanyPersonFilter.filterData
            ? parentCompanyPersonFilter.filterData
            : defaultState.excludedParentCompanyPersonFilterSupplierIds,
        searchFilters: newSearchFilters
      };
    case SEARCH_FILTER_REMOVE:
      const filtersToRemove: ReadonlyArray<SearchFilterNameValueType> = Array.isArray(action.searchFilterName)
        ? action.searchFilterName
        : [action.searchFilterName];
      const updatedSearchFilters = state.searchFilters.filter(
        (filter: SearchResultFilterType) => !filtersToRemove.includes(filter.name)
      );
      const existingParentCompanyPersonFilter = updatedSearchFilters.find(
        filter => filter.name === SEARCH_FILTER_NAME.PARENT_COMPANY_PERSON
      ) as SearchResultParentCompanyPersonFilterType;

      return {
        ...state,
        excludedParentCompanyPersonFilterSupplierIds:
          existingParentCompanyPersonFilter && existingParentCompanyPersonFilter.filterData
            ? existingParentCompanyPersonFilter.filterData
            : defaultState.excludedParentCompanyPersonFilterSupplierIds,
        searchFilters: updatedSearchFilters
      };
    case SEARCH_FILTER_RESET:
      return {
        ...state,
        excludedParentCompanyPersonFilterSupplierIds: defaultState.excludedParentCompanyPersonFilterSupplierIds,
        searchFilters: defaultState.searchFilters
      };

    case SEARCH_HAS_BEEN_RUN:
      return { ...state, hasSearchBeenRun: true };

    case SEARCH_MODE_CHANGE:
      return { ...state, searchMode: action.searchMode };

    case SEARCH_PHRASE_INPUT_VALUE_UPDATE:
      return {
        ...state,
        searchPhraseInputValue: action.searchPhraseInputValue
      };

    case SEARCH_RESULTS_RESET:
      return {
        ...state,
        appliedFilterChips: defaultState.appliedFilterChips,
        checkedFilterIdsMap: defaultState.checkedFilterIdsMap,
        currentSearchBuyerCompanyKey: defaultState.currentSearchBuyerCompanyKey,
        currentSearchCompanyCount: defaultState.currentSearchCompanyCount,
        currentSearchResultCompanyKeys: defaultState.currentSearchResultCompanyKeys,
        currentSearchResults: defaultState.currentSearchResults,
        currentSearchUserCount: defaultState.currentSearchUserCount,
        excludedParentCompanyPersonFilterSupplierIds: state.excludedParentCompanyPersonFilterSupplierIds,
        filterTreeDataMap: state.filterTreeDataMapDefault,
        invitedEventCountRangeEnd: defaultState.invitedEventCountRangeEnd,
        invitedEventCountRangeStart: defaultState.invitedEventCountRangeStart,
        invitedEventCountTreeData: defaultState.invitedEventCountTreeData,
        invitedPortalCountRangeEnd: defaultState.invitedPortalCountRangeEnd,
        invitedPortalCountRangeStart: defaultState.invitedPortalCountRangeStart,
        invitedPortalCountTreeData: defaultState.invitedPortalCountTreeData,
        isAlphabeticalOrderEnabled: defaultState.isAlphabeticalOrderEnabled,
        openFilterIdsMap: defaultState.openFilterIdsMap,
        searchFilters: defaultState.searchFilters,
        searchPhraseForLastSearch: defaultState.searchPhraseForLastSearch,
        selectedListKey: defaultState.selectedListKey,
        viewMode: defaultState.viewMode
      };
    case SEARCH_RESULTS_SET_COMPANY_COUNT:
      return { ...state, currentSearchCompanyCount: action.companyCount };
    case SEARCH_RESULTS_SET_ALPHABETICAL_ORDER_ENABLED:
      return { ...state, isAlphabeticalOrderEnabled: action.isEnabled };
    case SEARCH_RESULTS_SET_LOADING:
      return { ...state, areSearchResultsLoading: action.isLoading };
    case SEARCH_RESULTS_SET_SEARCH_PARAMS:
      return {
        ...state,
        currentSearchBuyerCompanyKey: action.buyerCompanyKey,
        searchPhraseForLastSearch: action.searchPhrase
      };
    case SEARCH_RESULTS_SET_COMPANY_SEARCH_ENABLED:
      return { ...state, isCompanySearch: action.isCompanySearch };
    case SEARCH_RESULTS_SET_VIEW_MODE:
      return { ...state, viewMode: action.viewMode };
    case SEARCH_RESULTS_UPDATE:
      /* Apply filters except the show companies filter to the data to use the results to get the
         user and company counts. Can't actually use these filtered results as the currentSearchResults
         because then applying filters loses the node open info managed by the TreeDataProvider.
         This filter function also does not use the show users button filter since hidden users
         should still be a part of the user count.
         Make sure when doing a SEARCH_FILTER_ADD/REMOVE to call SEARCH_RESULTS_UPDATE
         so that the counts get updated as well. */
      const filteredSearchResults = getFilteredSearchResults(action.searchResults, state.searchFilters);
      return {
        ...state,
        currentSearchResultCompanyKeys: getUniqueCompanyKeysFromSearchResults(action.searchResults),
        currentSearchResults: action.searchResults,
        currentSearchUserCount: getUserCountFromSearchResults(filteredSearchResults),
        currentSearchCompanyCount: getCompanyCountFromSearchResults(filteredSearchResults)
      };

    case SUPPLIER_SCHEMA_NAME_SET:
      return {
        ...state,
        currentSupplierIntelSchemaName: action.supplierIntelSchemaName
      };

    default:
      return state;
  }
}

//
// --- Helper Functions ---

async function loadFilterData(isIPAFB: boolean): Promise<LoadFilterDataReturnValue> {
  const [
    searchFiltersResponse,
    showPermissionedFiltersResponse,
    supplierIntelSchemaNameResponse,
    countryListResponse,
    diversityTypeMapResponse
  ] = await Promise.all([
    axios.request(APIConfig.SearchFilter.getSearchFilters(isIPAFB)),
    axios.request(
      APIConfig.Permission.hasScreenPermission(PRODUCT.SUPPLIER_INTELLIGENCE, SCREEN_DESCRIPTION.VIEW_SUPPLIER)
    ),
    axios.request(APIConfig.SearchFilter.getSupplierIntelSchemaName()),

    axios.request(APIConfig.SupplierSearch.getCountryList()),
    axios.request(APIConfig.SupplierSearch.getDiversityTypeMap())
  ]);

  return {
    countryList: countryListResponse.data ? countryListResponse.data : [],
    diversityTypeMap: diversityTypeMapResponse.data ? diversityTypeMapResponse.data : {},
    hasViewSupplierPermission: !!showPermissionedFiltersResponse.data,
    supplierIntelSchemaName:
      supplierIntelSchemaNameResponse.data === ITEM_SCHEMA_NAME.POWERADVOCATE || !supplierIntelSchemaNameResponse.data
        ? ''
        : supplierIntelSchemaNameResponse.data,
    rawFilterRootNode: searchFiltersResponse.data ? searchFiltersResponse.data : {}
  };
}

async function loadPowerScoreResultsForPeople(
  buyerCompanyKey: number,
  personIds: ReadonlyArray<number>
): Promise<ReadonlyArray<any>> {
  // if no buyerCompanyKey or no personIds, do not need to run these requests
  if (!buyerCompanyKey || !Array.isArray(personIds) || !personIds.length) {
    return [];
  }

  // We need to override default page size so we can get all results back in one request.
  // This assumes that if we get a result set back from a search or list query, it will
  // return a manageable amount for SIQS. Therefore, we are just using the number of
  // unique person ids instead of running an initial request to get a "COUNT" (number of actual results)
  // response and using a more accurate limit.
  const powerScoreResponse = await axios.request(
    APIConfig.SpendQuery.getPointOfContactScoreForPeople(buyerCompanyKey, personIds, { pageSize: personIds.length })
  );

  return powerScoreResponse && Array.isArray(powerScoreResponse?.data?.rows) ? powerScoreResponse.data.rows : [];
}

async function getSupplierVendorMappingsForList(listData: any) {
  const eipCompanyKeys: ReadonlyArray<number> = listData.supplierCompanies
    ? listData.supplierCompanies
        .map((company: any) => parseInt(company.companyKey))
        .filter((companyKey: number) => !!companyKey)
    : [];

  const supplierVendorMappingResponse = await axios.request(
    APIConfig.Supplier.getSupplierVendorMappings(eipCompanyKeys)
  );

  return supplierVendorMappingResponse && Array.isArray(supplierVendorMappingResponse.data)
    ? (supplierVendorMappingResponse.data as ReadonlyArray<any>)
    : [];
}

interface SearchContextProviderProps {
  isDiscovery: boolean;
  personCompany: CompanyType;
  searchTerm?: string;
}

const oneLayerTrees = [FILTER_TREE_TYPE.INVITED_EVENT_COUNT, FILTER_TREE_TYPE.SOURCE];

/**
 * Search Context Provider Component
 *
 * Sets context value to an array where the first element is a data object related to searches in the
 * application and the second element is an action object (or object with a bunch of functions) that can
 * be used to trigger actions on search data.
 */

export const SearchContextProvider: React.FC<SearchContextProviderProps> = props => {
  const { children, isDiscovery, personCompany, searchTerm } = props;

  const [state, dispatch] = React.useReducer(
    searchContextReducer,
    defaultState,
    initializeState({ isDiscovery, searchTerm })
  );
  const [eventBusClient] = useEventBus();
  const { offeringKey } = useQueryParameters();

  //
  // --- Initial Data Load ---

  React.useEffect(() => {
    async function searchContextSetup() {
      dispatch({ type: FILTER_TREE_DATA_SET_LOADING, isLoading: true });

      try {
        const { countryList, diversityTypeMap, hasViewSupplierPermission, rawFilterRootNode, supplierIntelSchemaName } =
          await loadFilterData(state.searchMode === SEARCH_MODE.IPAFB_SUPPLIER_SEARCH);

        //
        // --- Calculate Filter Data ---

        const productServiceFilterTreeNode = rawFilterRootNode.children.find(
          (childFilterTreeNode: FilterTreeNode) => childFilterTreeNode.name === 'Products and Services'
        );

        const qualificationFilterTreeNodes: ReadonlyArray<FilterTreeNode> = hasViewSupplierPermission
          ? rawFilterRootNode.children.filter(
              (childFilterTreeNode: FilterTreeNode) =>
                childFilterTreeNode.name && childFilterTreeNode.name.includes('Qualification')
            )
          : [];

        const diversityClassificationFilterTreeNode: FilterTreeNode | undefined = hasViewSupplierPermission
          ? rawFilterRootNode.children.find(
              (childFilterTreeNode: FilterTreeNode) =>
                childFilterTreeNode.name && childFilterTreeNode.name.includes('Diversity Classification')
            )
          : undefined;
        const filteredDiversityClassificationTreeNode =
          personCompany.companyKey !== COMPANY_KEY.POWERADVOCATE &&
          diversityClassificationFilterTreeNode &&
          diversityClassificationFilterTreeNode.children
            ? {
                ...diversityClassificationFilterTreeNode,
                children: diversityClassificationFilterTreeNode.children.filter(
                  treeNode => treeNode.name !== 'PowerAdvocate'
                )
              }
            : diversityClassificationFilterTreeNode;

        const segmentationFilterTreeNode: FilterTreeNode | undefined = hasViewSupplierPermission
          ? rawFilterRootNode.children.find(
              (childFilterTreeNode: FilterTreeNode) => childFilterTreeNode.name === 'Segmentation'
            )
          : undefined;

        //
        // --- Dispatch Data Changes ---

        dispatch({ type: SUPPLIER_SCHEMA_NAME_SET, supplierIntelSchemaName });
        dispatch({
          type: FILTER_TREE_DATA_UPDATE,
          filterTreeDataMap: {
            [FILTER_TREE_TYPE.COUNTRY_OR_STATE]: transformCountryListToTreeNodes(countryList),
            [FILTER_TREE_TYPE.DIVERSE]: transformDiversityTypeMapToTreeNodes(diversityTypeMap),
            [FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION]: transformRawFilterNodeCollectionToTreeNodes(
              filteredDiversityClassificationTreeNode ? [filteredDiversityClassificationTreeNode] : [],
              FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION
            ),
            [FILTER_TREE_TYPE.ITEM]: transformRawFilterNodeCollectionToTreeNodes(
              productServiceFilterTreeNode ? [productServiceFilterTreeNode] : [],
              FILTER_TREE_TYPE.ITEM
            ),
            [FILTER_TREE_TYPE.QUALIFICATION]: transformRawFilterNodeCollectionToTreeNodes(
              qualificationFilterTreeNodes,
              FILTER_TREE_TYPE.QUALIFICATION
            ),
            [FILTER_TREE_TYPE.SEGMENTATION]: transformRawFilterNodeCollectionToTreeNodes(
              segmentationFilterTreeNode ? [segmentationFilterTreeNode] : [],
              FILTER_TREE_TYPE.SEGMENTATION
            )
          }
        });
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            'Error while loading search filter options',
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      } finally {
        dispatch({ type: FILTER_TREE_DATA_SET_LOADING, isLoading: false });
      }
    }

    searchContextSetup();
  }, [eventBusClient, personCompany.companyKey, state.searchMode]);

  //
  //  --- Helper Functions ---

  // put at the top because other helpers use this function
  const executeSearchResultsReset = React.useCallback(() => {
    dispatch({ type: SEARCH_RESULTS_RESET });
  }, []);

  const executeDiscoveryFiltersForLists = React.useCallback(
    async (discoveryFilterIds: DiscoverySearchFilterOptionsType) => {
      if (state.currentSearchResults.length === 0) return;

      dispatch({ type: SEARCH_RESULTS_SET_LOADING, isLoading: true });

      const {
        countryOrStateFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.COUNTRY_OR_STATE],
        diversityClassificationFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION],
        diversityFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.DIVERSE],
        invitedEventCountFilters = getSupplierActivityCountFiltersFromRange(
          state.invitedEventCountRangeStart,
          state.invitedEventCountRangeEnd
        ),
        invitedPortalCountFilters = getSupplierActivityCountFiltersFromRange(
          state.invitedPortalCountRangeStart,
          state.invitedPortalCountRangeEnd
        ),
        itemFilterIds = state.checkedFilterIdsMap[FILTER_TREE_TYPE.ITEM],
        qualificationFilterIds = state.checkedFilterIdsMap[FILTER_TREE_TYPE.QUALIFICATION],
        segmentationFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.SEGMENTATION],
        sourceFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.SOURCE]
      } = discoveryFilterIds;

      try {
        if (
          !!diversityClassificationFilters.length ||
          !!diversityFilters.length ||
          !!invitedEventCountFilters.length ||
          !!invitedPortalCountFilters.length ||
          !!countryOrStateFilters.length ||
          !!itemFilterIds.length ||
          !!qualificationFilterIds.length ||
          !!segmentationFilters.length ||
          !!sourceFilters.length
        ) {
          // create discovery filter map
          const currentSearchResults = [...state.currentSearchResults];
          const listOfEipCompanyKeys = getCompanyKeysFromGroupedSupplierList(currentSearchResults);
          const countryFilters = getCountryIdsFromCountryStateList(countryOrStateFilters);
          const stateFilters = getStateIdsFromCountryStateList(countryOrStateFilters);
          const itemFilters = convertStringListToIntegerList(itemFilterIds);
          const supplierIntelSchemaName = state.currentSupplierIntelSchemaName;
          const matchesDiscoveryFiltersEipCompaniesMap = (
            await axios.request(
              APIConfig.SupplierSearch.getMatchesDiscoveryFiltersMapForEipCompanyKeysIds({
                countryFilters,
                diversityClassificationFilters,
                diversityFilters,
                eipCompanyKeys: listOfEipCompanyKeys,
                invitedEventCountFilters,
                invitedPortalCountFilters,
                itemFilters,
                qualificationFilters: qualificationFilterIds,
                segmentationFilters,
                sourceFilters,
                stateFilters,
                supplierIntelSchemaName
              })
            )
          ).data;

          const appliedFilterStatusEipCompanies = addMatchesDiscoveryFiltersToEipCompanyList(
            [...state.currentSearchResults],
            matchesDiscoveryFiltersEipCompaniesMap
          );
          dispatch({
            type: SEARCH_RESULTS_UPDATE,
            searchResults: appliedFilterStatusEipCompanies
          });
        } else {
          dispatch({
            type: SEARCH_RESULTS_UPDATE,
            searchResults: state.currentSearchResults
          });
        }
      } catch (error) {
        executeSearchResultsReset();
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            'Error while loading diverse list members',
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      } finally {
        dispatch({ type: SEARCH_RESULTS_SET_LOADING, isLoading: false });
      }
    },
    [
      eventBusClient,
      executeSearchResultsReset,
      state.checkedFilterIdsMap,
      state.currentSearchResults,
      state.currentSupplierIntelSchemaName,
      state.invitedEventCountRangeEnd,
      state.invitedEventCountRangeStart,
      state.invitedPortalCountRangeEnd,
      state.invitedPortalCountRangeStart
    ]
  );

  const executeDiscoveryFiltersForFrequentContactsList = React.useCallback(
    async (discoveryFilterIds: DiscoverySearchFilterOptionsType) => {
      if (state.currentSearchResults.length === 0) return;

      dispatch({ type: SEARCH_RESULTS_SET_LOADING, isLoading: true });

      const {
        countryOrStateFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.COUNTRY_OR_STATE],
        diversityClassificationFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION],
        diversityFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.DIVERSE],
        invitedEventCountFilters = getSupplierActivityCountFiltersFromRange(
          state.invitedEventCountRangeStart,
          state.invitedEventCountRangeEnd
        ),
        invitedPortalCountFilters = getSupplierActivityCountFiltersFromRange(
          state.invitedPortalCountRangeStart,
          state.invitedPortalCountRangeEnd
        ),
        itemFilterIds = state.checkedFilterIdsMap[FILTER_TREE_TYPE.ITEM],
        qualificationFilterIds = state.checkedFilterIdsMap[FILTER_TREE_TYPE.QUALIFICATION],
        segmentationFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.SEGMENTATION],
        sourceFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.SOURCE]
      } = discoveryFilterIds;

      try {
        if (
          !!countryOrStateFilters.length ||
          !!diversityClassificationFilters.length ||
          !!diversityFilters.length ||
          !!invitedEventCountFilters.length ||
          !!invitedPortalCountFilters.length ||
          !!itemFilterIds.length ||
          !!qualificationFilterIds.length ||
          !!segmentationFilters.length ||
          !!sourceFilters.length
        ) {
          // create discovery filter map
          const frequentContactsList = [...state.currentSearchResults];
          const listOfSupplierIds =
            Array.isArray(frequentContactsList) && frequentContactsList.length > 0
              ? frequentContactsList.reduce(
                  (allSupplierIds, currentFrequentContact) =>
                    allSupplierIds.concat(getAllSupplierIdsForSupplierTree(currentFrequentContact)),
                  []
                )
              : [];
          const countryFilters = getCountryIdsFromCountryStateList(countryOrStateFilters);
          const stateFilters = getStateIdsFromCountryStateList(countryOrStateFilters);
          const itemFilters = convertStringListToIntegerList(itemFilterIds);
          const supplierIntelSchemaName = state.currentSupplierIntelSchemaName;
          const matchesDiscoveryFiltersSupplierIdsMap = (
            await axios.request(
              APIConfig.SupplierSearch.getMatchesDiscoveryFiltersMapForSupplierIds({
                countryFilters,
                diversityClassificationFilters,
                diversityFilters,
                invitedEventCountFilters,
                invitedPortalCountFilters,
                itemFilters,
                qualificationFilters: qualificationFilterIds,
                segmentationFilters,
                sourceFilters,
                stateFilters,
                supplierIds: listOfSupplierIds,
                supplierIntelSchemaName
              })
            )
          ).data;

          const appliedFilterStatusPowerScoreContacts = addMatchesDiscoveryFiltersToFrequentContactList(
            [...state.currentSearchResults],
            matchesDiscoveryFiltersSupplierIdsMap
          );
          dispatch({
            type: SEARCH_RESULTS_UPDATE,
            searchResults: appliedFilterStatusPowerScoreContacts
          });
        } else {
          dispatch({
            type: SEARCH_RESULTS_UPDATE,
            searchResults: state.currentSearchResults
          });
        }
      } catch (error) {
        executeSearchResultsReset();
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            'Error while loading diverse list members',
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      } finally {
        dispatch({ type: SEARCH_RESULTS_SET_LOADING, isLoading: false });
      }
    },
    [
      eventBusClient,
      executeSearchResultsReset,
      state.checkedFilterIdsMap,
      state.currentSearchResults,
      state.currentSupplierIntelSchemaName,
      state.invitedEventCountRangeEnd,
      state.invitedEventCountRangeStart,
      state.invitedPortalCountRangeEnd,
      state.invitedPortalCountRangeStart
    ]
  );

  const executeFrequentContactRequest = React.useCallback(
    async (buyerCompanyKey: string) => {
      try {
        dispatch({ type: SEARCH_RESULTS_SET_LOADING, isLoading: true });

        const response = await axios.request(
          APIConfig.SpendQuery.getFrequentContactScores(buyerCompanyKey, { pageSize: 50 })
        );

        let frequentContactData = response.data.rows;

        // if less than 50 of score more than 0 then good, otherwise need to grab all tied with score of 50th.
        if (frequentContactData.length === 50) {
          const countResponse = await axios.request(
            APIConfig.SpendQuery.getFrequentContactScores(buyerCompanyKey, {
              pointOfContactScore: frequentContactData[49].poc - 1,
              spendQuery: {
                columns: {
                  count: { fields: ['PERSONKEY'], type: 'COUNT_DISTINCT' }
                },
                groupBy: []
              },
              useDefaultColumns: false
            })
          );

          const count = countResponse.data.rows[0].count;

          const additionalResponse = await axios.request(
            APIConfig.SpendQuery.getFrequentContactScores(buyerCompanyKey, {
              pageSize: count,
              pointOfContactScore: frequentContactData[49].poc - 1
            })
          );

          frequentContactData = additionalResponse.data.rows;
        }

        const personKeys = frequentContactData.map((frequentContactDataItem: any) => frequentContactDataItem.personKey);
        const activeInvitedSuppliersResponse = await axios.request(
          APIConfig.SupplierList.getActiveInvitedSupplierPersonsByIds(personKeys)
        );

        // filter out non active/invited supplier users and append lastLoginSuccess data to people.
        if (Array.isArray(activeInvitedSuppliersResponse.data) && activeInvitedSuppliersResponse.data.length) {
          const activeInvitedSupplierKeyMap = new Map(
            activeInvitedSuppliersResponse.data.map(user => [
              user.personKey,
              { lastLoginSuccess: user.lastLoginSuccess }
            ])
          );
          frequentContactData = frequentContactData
            .filter((person: any) => !!activeInvitedSupplierKeyMap.get(person.personKey))
            .map((person: any) => {
              const currentActiveInvitedSupplierPerson = activeInvitedSupplierKeyMap.get(person.personKey);
              return {
                ...person,
                lastLoginSuccess: currentActiveInvitedSupplierPerson
                  ? currentActiveInvitedSupplierPerson.lastLoginSuccess
                  : undefined
              };
            });
        }

        const frequentContactTreeNodes = transformPowerScoreContactListToTreeNodes(frequentContactData);

        dispatch({
          type: SEARCH_RESULTS_UPDATE,
          searchResults: frequentContactTreeNodes
        });
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            'Error while loading frequent contacts',
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      } finally {
        dispatch({ type: SEARCH_RESULTS_SET_LOADING, isLoading: false });
      }
    },
    [eventBusClient]
  );

  const executeListMemberRequestWithConfig = React.useCallback(
    async (apiRequestConfig: AxiosRequestConfig, buyerCompanyKey: number) => {
      const countryOrStateFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.COUNTRY_OR_STATE];
      const diversityClassificationFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION];
      const diversityFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.DIVERSE];
      const invitedEventCountFilters = getSupplierActivityCountFiltersFromRange(
        state.invitedEventCountRangeStart,
        state.invitedEventCountRangeEnd
      );
      const invitedPortalCountFilters = getSupplierActivityCountFiltersFromRange(
        state.invitedPortalCountRangeStart,
        state.invitedPortalCountRangeEnd
      );
      const itemFilterIds = state.checkedFilterIdsMap[FILTER_TREE_TYPE.ITEM];
      const qualificationFilterIds = state.checkedFilterIdsMap[FILTER_TREE_TYPE.QUALIFICATION];
      const segmentationFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.SEGMENTATION];
      const sourceFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.SOURCE];

      try {
        dispatch({ type: SEARCH_RESULTS_SET_LOADING, isLoading: true });
        const listMemberResponse = await axios.request(apiRequestConfig);
        const listData = listMemberResponse.data;

        const supplierVendorMappings = await getSupplierVendorMappingsForList(listData);

        let listTreeNodes = transformSupplierListMembersToSearchResult(listData, supplierVendorMappings, {
          excludePeople: false
        });
        // gather and apply PowerScore data if it applies
        const personIds = getUniquePersonIdsFromSearchResults(listTreeNodes);
        const powerScoreResults = await loadPowerScoreResultsForPeople(buyerCompanyKey, personIds);

        if (powerScoreResults.length > 0) {
          listTreeNodes = mergeTreeNodesAndPowerScoreResults(listTreeNodes, powerScoreResults);
        }

        if (
          !!diversityClassificationFilters.length ||
          !!diversityFilters.length ||
          !!invitedEventCountFilters.length ||
          !!invitedPortalCountFilters.length ||
          !!segmentationFilters.length
        ) {
          const listOfEipCompanyKeys = getCompanyKeysFromGroupedSupplierList(listTreeNodes);
          const countryFilters = getCountryIdsFromCountryStateList(countryOrStateFilters);
          const stateFilters = getStateIdsFromCountryStateList(countryOrStateFilters);
          const itemFilters = convertStringListToIntegerList(itemFilterIds);
          const supplierIntelSchemaName = state.currentSupplierIntelSchemaName;
          const matchesDiscoveryFiltersEipCompaniesMap = (
            await axios.request(
              APIConfig.SupplierSearch.getMatchesDiscoveryFiltersMapForEipCompanyKeysIds({
                countryFilters,
                diversityClassificationFilters,
                diversityFilters,
                eipCompanyKeys: listOfEipCompanyKeys,
                invitedEventCountFilters,
                invitedPortalCountFilters,
                itemFilters,
                qualificationFilters: qualificationFilterIds,
                segmentationFilters,
                sourceFilters,
                stateFilters,
                supplierIntelSchemaName
              })
            )
          ).data;
          const appliedFilterStatusEipCompanies = addMatchesDiscoveryFiltersToEipCompanyList(
            listTreeNodes,
            matchesDiscoveryFiltersEipCompaniesMap
          );

          dispatch({
            type: SEARCH_RESULTS_UPDATE,
            searchResults: appliedFilterStatusEipCompanies
          });
        } else {
          dispatch({
            type: SEARCH_RESULTS_UPDATE,
            searchResults: listTreeNodes
          });
        }
      } catch (error) {
        executeSearchResultsReset();
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            'Error while loading list members',
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      } finally {
        dispatch({ type: SEARCH_RESULTS_SET_LOADING, isLoading: false });
      }
    },
    [
      eventBusClient,
      executeSearchResultsReset,
      state.checkedFilterIdsMap,
      state.currentSupplierIntelSchemaName,
      state.invitedEventCountRangeEnd,
      state.invitedEventCountRangeStart,
      state.invitedPortalCountRangeEnd,
      state.invitedPortalCountRangeStart
    ]
  );

  const executeSearch = React.useCallback(
    async (searchPhrase: string, searchOptions: SearchOptionsType = {}) => {
      const {
        buyerCompanyKey = state.currentSearchBuyerCompanyKey,
        countryOrStateFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.COUNTRY_OR_STATE],
        diversityClassificationFilters = generateDiverstiyClassificationFilters(
          state.checkedFilterIdsMap[FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION],
          state.filterTreeDataMap[FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION]
        ),
        diversityFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.DIVERSE],
        excludePeople = isDiscovery,
        invitedEventCountFilters = getSupplierActivityCountFiltersFromRange(
          state.invitedEventCountRangeStart,
          state.invitedEventCountRangeEnd
        ),
        invitedPortalCountFilters = getSupplierActivityCountFiltersFromRange(
          state.invitedPortalCountRangeStart,
          state.invitedPortalCountRangeEnd
        ),
        isCompanySearch = state.isCompanySearch,
        itemFilterIds = state.checkedFilterIdsMap[FILTER_TREE_TYPE.ITEM],
        qualificationFilterIds = state.checkedFilterIdsMap[FILTER_TREE_TYPE.QUALIFICATION],
        segmentationFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.SEGMENTATION],
        sourceFilters = state.checkedFilterIdsMap[FILTER_TREE_TYPE.SOURCE],
        supplierIntelSchemaName = state.currentSupplierIntelSchemaName,
        viewMode = state.viewMode,
        useNewSupplierSearch = false
      } = searchOptions;

      try {
        if (cancel) {
          cancel();
        }

        dispatch({ type: SEARCH_RESULTS_SET_LOADING, isLoading: true });
        dispatch({ type: SEARCH_RESULTS_SET_SEARCH_PARAMS, buyerCompanyKey, searchPhrase });
        dispatch({
          type: SEARCH_FILTER_REMOVE,
          searchFilterName: [
            SEARCH_FILTER_NAME.ACTIVE_USER,
            SEARCH_FILTER_NAME.PERSON_NAME,
            SEARCH_FILTER_NAME.POWERSCORE
          ]
        });
        dispatch({
          type: SEARCH_FILTER_ADD,
          searchFilter: { ...searchResultParentCompanyPersonFilter, filterData: [] }
        });
        const countryFilters = getCountryIdsFromCountryStateList(countryOrStateFilters);
        const stateFilters = getStateIdsFromCountryStateList(countryOrStateFilters);
        const itemFilters = convertStringListToIntegerList(itemFilterIds);
        const searchPhraseUsed = searchPhrase && searchPhrase.length > 0;
        const filtersUsed =
          countryFilters.length > 0 ||
          diversityClassificationFilters.length > 0 ||
          diversityFilters.length > 0 ||
          invitedEventCountFilters.length > 0 ||
          invitedPortalCountFilters.length > 0 ||
          itemFilters.length > 0 ||
          qualificationFilterIds.length > 0 ||
          segmentationFilters.length > 0 ||
          sourceFilters.length > 0 ||
          stateFilters.length > 0;

        if (searchPhraseUsed || filtersUsed) {
          const searchResponse = await axios.request({
            ...APIConfig.Search.runSearch(searchPhrase, {
              countryFilters,
              diversityClassificationFilters,
              diversityFilters,
              excludePeople,
              invitedEventCountFilters,
              invitedPortalCountFilters,
              isCompanySearch,
              itemFilters,
              qualificationFilters: qualificationFilterIds,
              segmentationFilters,
              sourceFilters,
              stateFilters,
              supplierIntelSchemaName,
              viewMode,
              useNewSupplierSearch
            }),
            cancelToken: new CancelToken(function executor(c) {
              cancel = c;
            })
          });
          let searchResults = searchResponse.data.searchResults;
          let treeNodesShouldOpen = searchResponse.data.hasContactMatchingSearchTerm;
          if (!excludePeople && buyerCompanyKey) {
            const personIds = getUniquePersonIdsFromSearchResults(searchResults);
            const powerScoreResults = await loadPowerScoreResultsForPeople(buyerCompanyKey, personIds);
            searchResults =
              powerScoreResults.length > 0
                ? mergeTreeNodesAndPowerScoreResults(searchResults, powerScoreResults)
                : searchResults;
          }
          dispatch({ type: SEARCH_HAS_BEEN_RUN });
          const treeNodeSearchResults = transformSearchResultToTreeNodes(searchResults, { treeNodesShouldOpen });

          dispatch({
            type: SEARCH_RESULTS_UPDATE,
            searchResults: state.isAlphabeticalOrderEnabled
              ? getSearchResultsSortedAlphabetically(treeNodeSearchResults)
              : getSearchResultsSortedByRelevancyScore(treeNodeSearchResults)
          });
        } else {
          // Empty search results.
          dispatch({
            type: SEARCH_RESULTS_UPDATE,
            searchResults: []
          });
        }
        dispatch({ type: SEARCH_RESULTS_SET_LOADING, isLoading: false });
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('cancelled');
        } else {
          executeSearchResultsReset();
          eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
            message: createRequestErrorMessage(
              'Error while searching for suppliers',
              axios.isAxiosError(error) ? error.response : undefined
            )
          });
          dispatch({ type: SEARCH_RESULTS_SET_LOADING, isLoading: false });
        }
      }
    },
    [
      eventBusClient,
      executeSearchResultsReset,
      isDiscovery,
      state.checkedFilterIdsMap,
      state.currentSearchBuyerCompanyKey,
      state.currentSupplierIntelSchemaName,
      state.invitedEventCountRangeEnd,
      state.invitedEventCountRangeStart,
      state.invitedPortalCountRangeEnd,
      state.invitedPortalCountRangeStart,
      state.isAlphabeticalOrderEnabled,
      state.isCompanySearch,
      state.viewMode,
      state.filterTreeDataMap
    ]
  );

  const updateAppliedFilters = React.useCallback(
    (
      appliedFilters: ReadonlyArray<AppliedFilterChipType>,
      checkedFilterIds: ReadonlyArray<string>,
      filterTreeType: FilterTreeType
    ) => {
      const combinedCheckedFilterIds = Object.entries(state.checkedFilterIdsMap)
        .map(([filterType, checkedIds]) => (filterType === filterTreeType ? checkedFilterIds : checkedIds))
        .reduce((acc, checkedIds) => [...acc, ...checkedIds]);

      const combinedTreeData = Object.values(state.filterTreeDataMap).reduce((acc, treeData) => [...acc, ...treeData]);

      // always remove applied filters if matching node is no longer checked (but we don't always add when checked)
      const remainingAppliedFilters = appliedFilters.filter(appliedFilter =>
        combinedCheckedFilterIds.includes(appliedFilter.id)
      );

      // roll up filters to parents if all children selected
      const rolledUpAppliedFilters = getRolledUpAppliedFilters(
        combinedTreeData,
        remainingAppliedFilters,
        combinedCheckedFilterIds
      );

      // update allChildrenChecked and someChildrenChecked status on each applied filter
      const newAppliedFilters = getAppliedFilterChipsWithUpdatedChildrenStatus(
        combinedTreeData,
        rolledUpAppliedFilters,
        combinedCheckedFilterIds
      );
      dispatch({
        type: APPLIED_FILTER_CHIPS_UPDATE,
        appliedFilterChips: newAppliedFilters,
        checkedFilterIds: combinedCheckedFilterIds
      });
    },
    [state.checkedFilterIdsMap, state.filterTreeDataMap]
  );

  const updateSearchResultsWithFilters = React.useCallback(
    (buyerCompanyKey: number, filters: ReadonlyArray<string>, filterTreeType: FilterTreeType) => {
      let filterString: string = '';
      let searchFilterParam: ReadonlyArray<string> | readonly DiversityClassificationFilter[] = filters;
      if (filterTreeType === FILTER_TREE_TYPE.COUNTRY_OR_STATE) {
        filterString = 'countryOrStateFilters';
      } else if (filterTreeType === FILTER_TREE_TYPE.DIVERSE) {
        filterString = 'diversityFilters';
      } else if (filterTreeType === FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION) {
        filterString = 'diversityClassificationFilters';
        searchFilterParam = generateDiverstiyClassificationFilters(
          filters,
          state.filterTreeDataMap[FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION]
        );
      } else if (filterTreeType === FILTER_TREE_TYPE.INVITED_EVENT_COUNT) {
        filterString = 'invitedEventCountFilterIds';
      } else if (filterTreeType === FILTER_TREE_TYPE.INVITED_PORTAL_COUNT) {
        filterString = 'invitedPortalCountFilterIds';
      } else if (filterTreeType === FILTER_TREE_TYPE.ITEM) {
        filterString = 'itemFilterIds';
      } else if (filterTreeType === FILTER_TREE_TYPE.QUALIFICATION) {
        filterString = 'qualificationFilterIds';
      } else if (filterTreeType === FILTER_TREE_TYPE.SEGMENTATION) {
        filterString = 'segmentationFilters';
      } else if (filterTreeType === FILTER_TREE_TYPE.SOURCE) {
        filterString = 'sourceFilters';
      }
      if (state.searchPhraseForLastSearch) {
        executeSearch(state.searchPhraseForLastSearch, {
          buyerCompanyKey,
          excludePeople: isDiscovery,
          viewMode: state.viewMode,
          [filterString]: searchFilterParam,
          useNewSupplierSearch: state.searchMode == SEARCH_MODE.IPAFB_SUPPLIER_SEARCH
        });
      } else if (state.selectedListCategoryId === LIST_CATEGORY_ID.POWER_SCORE_CONTACTS) {
        executeDiscoveryFiltersForFrequentContactsList({
          [filterString]: filters
        });
      } else if (state.searchMode === SEARCH_MODE.COMPANY_LISTS) {
        executeDiscoveryFiltersForLists({
          [filterString]: filters
        });
      } else if (!isDiscovery) {
        // search will return empty results if n o search filter nor searchterm is given.
        executeSearch('', {
          buyerCompanyKey,
          excludePeople: isDiscovery,
          viewMode: state.viewMode,
          [filterString]: searchFilterParam,
          useNewSupplierSearch: state.searchMode == SEARCH_MODE.IPAFB_SUPPLIER_SEARCH
        });
      }
    },
    [
      executeDiscoveryFiltersForFrequentContactsList,
      executeDiscoveryFiltersForLists,
      executeSearch,
      isDiscovery,
      state.searchPhraseForLastSearch,
      state.searchMode,
      state.selectedListCategoryId,
      state.viewMode,
      state.filterTreeDataMap,
      state.checkedFilterIdsMap
    ]
  );

  const updateSearchResultsWithSupplierActivityFilters = React.useCallback(
    (
      filterName: keyof DiscoveryRequestFilterOptions,
      buyerCompanyKey: number,
      rangeStart: number,
      rangeEnd: number,
      useNewSupplierSearch: boolean
    ) => {
      // this should use common util function as noted in another comment
      const rangeFilterValues = getSupplierActivityCountFiltersFromRange(rangeStart, rangeEnd);

      if (state.searchPhraseForLastSearch) {
        executeSearch(state.searchPhraseForLastSearch, {
          buyerCompanyKey,
          excludePeople: isDiscovery,
          viewMode: state.viewMode,
          [filterName]: rangeFilterValues,
          useNewSupplierSearch
        });
      } else if (state.selectedListCategoryId === LIST_CATEGORY_ID.POWER_SCORE_CONTACTS) {
        executeDiscoveryFiltersForFrequentContactsList({ [filterName]: rangeFilterValues });
      } else if (state.searchMode === SEARCH_MODE.COMPANY_LISTS) {
        executeDiscoveryFiltersForLists({ [filterName]: rangeFilterValues });
      }
    },
    [
      executeDiscoveryFiltersForFrequentContactsList,
      executeDiscoveryFiltersForLists,
      executeSearch,
      isDiscovery,
      state.searchMode,
      state.searchPhraseForLastSearch,
      state.selectedListCategoryId,
      state.viewMode
    ]
  );

  //
  // --- SearchAction Functions / Handlers ---

  const addCheckedFilter = React.useCallback(
    (buyerCompanyKey: number, filterTreeType: FilterTreeType, filterId: string, useNewSupplierSearch: boolean) => {
      // update checked filter ids
      const treeNode = getFilterNodeById(state.filterTreeDataMap[filterTreeType], filterId);

      if (!treeNode) {
        return;
      }

      // tree types that only have a single layer of nodes under the root node
      const newCheckedFilterIds =
        filterTreeType in oneLayerTrees
          ? [...state.checkedFilterIdsMap[filterTreeType], filterId]
          : getCheckedFilterIdsAfterNodeChecked(
              state.filterTreeDataMap[filterTreeType],
              treeNode,
              state.checkedFilterIdsMap[filterTreeType]
            );

      // update checked status in tree data
      dispatch({
        type: CHECKED_FILTER_UPDATE,
        checkedFilterIds: newCheckedFilterIds,
        filterTreeType: filterTreeType
      });

      let tooltip;
      if (filterTreeType === FILTER_TREE_TYPE.INVITED_EVENT_COUNT) {
        const parentTreeNode = treeNode
          ? getFilterParentNode(state.filterTreeDataMap[filterTreeType], treeNode)
          : undefined;
        const invitedEventCountFilterChipTooltip = parentTreeNode && parentTreeNode.name ? parentTreeNode.name : '';
        tooltip = invitedEventCountFilterChipTooltip;
      } else if (filterTreeType === FILTER_TREE_TYPE.COUNTRY_OR_STATE) {
        tooltip = treeNode.name;
      } else if (filterTreeType === FILTER_TREE_TYPE.QUALIFICATION) {
        const parentTreeNode = treeNode
          ? getFilterParentNode(state.filterTreeDataMap[filterTreeType], treeNode)
          : undefined;
        tooltip = parentTreeNode && parentTreeNode.name;
      }

      // add applied filter if needed. First check if a parent node filter already exists
      const parentNodeIds = getFilterParentNodeIds(state.filterTreeDataMap[filterTreeType], treeNode.id);
      const appliedFilterChipIds = state.appliedFilterChips
        .filter(appliedFilter => appliedFilter.filterTreeType === filterTreeType)
        .map(appliedFilter => appliedFilter.id);

      // only if a parent node does not already exist in applied
      if (!parentNodeIds.some(parentNodeId => appliedFilterChipIds.includes(parentNodeId))) {
        // update applied filters
        const appliedFilterChip = getAppliedFilterChipForTreeNode(treeNode, tooltip ? { tooltip } : {});

        updateAppliedFilters([...state.appliedFilterChips, appliedFilterChip], newCheckedFilterIds, filterTreeType);
      } else {
        updateAppliedFilters(state.appliedFilterChips, newCheckedFilterIds, filterTreeType);
      }

      const searchFilter = treeTypeToSearchFilter[filterTreeType];
      dispatch({ type: SEARCH_FILTER_ADD, searchFilter });

      updateSearchResultsWithFilters(buyerCompanyKey, newCheckedFilterIds, filterTreeType);
    },
    [
      state.appliedFilterChips,
      state.checkedFilterIdsMap,
      state.filterTreeDataMap,
      updateAppliedFilters,
      updateSearchResultsWithFilters
    ]
  );

  const addSearchFilter = React.useCallback(
    (searchFilter: SearchResultFilterType) => {
      dispatch({ type: SEARCH_FILTER_ADD, searchFilter });
      dispatch({ type: SEARCH_RESULTS_UPDATE, searchResults: state.currentSearchResults });
    },
    [state.currentSearchResults]
  );

  const addSupplierParentCompanyFilterExclusion = React.useCallback(
    (supplierId: string) => {
      const parentCompanyFilter = state.searchFilters.find(
        filter => filter.name === SEARCH_FILTER_NAME.PARENT_COMPANY_PERSON
      ) as SearchResultParentCompanyPersonFilterType;

      // SEARCH_FILTER_ADD also replaces a filter if already set, but we do need to update filterData to append the new
      // supplierId to the exiting filterData set (if its not already in there)
      const existingFilterData =
        parentCompanyFilter && parentCompanyFilter.filterData ? parentCompanyFilter.filterData : [];

      if (!existingFilterData.includes(supplierId)) {
        dispatch({
          type: SEARCH_FILTER_ADD,
          searchFilter: { ...searchResultParentCompanyPersonFilter, filterData: [...existingFilterData, supplierId] }
        });
        // update search results after adding a filter
        dispatch({
          type: SEARCH_RESULTS_UPDATE,
          searchResults: state.currentSearchResults
        });
      }
    },
    [state.currentSearchResults, state.searchFilters]
  );

  const removeSearchFilter = React.useCallback(
    (searchFilterName: SearchFilterNameValueType | ReadonlyArray<SearchFilterNameValueType>) => {
      dispatch({ type: SEARCH_FILTER_REMOVE, searchFilterName });
      // update search results as well after removing a filter
      dispatch({ type: SEARCH_RESULTS_UPDATE, searchResults: state.currentSearchResults });
    },
    [state.currentSearchResults]
  );

  const removeCheckedFilter = React.useCallback(
    (
      buyerCompanyKey: number,
      filterTreeType: FilterTreeType,
      filterId: string,
      useNewSupplierSearch: boolean,
      opts: RemoveCheckedFilterOptions = {}
    ) => {
      const { removeAll = false } = opts;

      const treeNode = getFilterNodeById(state.filterTreeDataMap[filterTreeType], filterId);

      if (!treeNode) {
        return;
      }

      const newCheckedFilterIds = removeAll
        ? []
        : getCheckedFilterIdsAfterNodeUnchecked(state.checkedFilterIdsMap[filterTreeType], treeNode);

      const searchFilterName = treeTypeToSearchFilter[filterTreeType].name;
      if (newCheckedFilterIds.length === 0) {
        dispatch({ type: SEARCH_FILTER_REMOVE, searchFilterName });
      }

      // update checked filters
      dispatch({
        type: CHECKED_FILTER_UPDATE,
        checkedFilterIds: newCheckedFilterIds,
        filterTreeType
      });

      updateAppliedFilters(state.appliedFilterChips, newCheckedFilterIds, filterTreeType);
      updateSearchResultsWithFilters(buyerCompanyKey, newCheckedFilterIds, filterTreeType);
    },
    [
      state.appliedFilterChips,
      state.checkedFilterIdsMap,
      state.filterTreeDataMap,
      updateAppliedFilters,
      updateSearchResultsWithFilters
    ]
  );

  const resetSearchFilters = React.useCallback(() => {
    dispatch({ type: SEARCH_FILTER_RESET });
  }, []);

  const runListMemberRequest = (listName: ListNameItemType, buyerCompanyKey: number) => {
    dispatch({ type: LIST_SET_SELECTED, listKey: listName.key });

    if (listName.listCategoryId === LIST_CATEGORY_ID.BID_EVENT_ITEMS_LIST) {
      runBidEventItemListMemberRequest(listName.key, buyerCompanyKey);
    } else if (listName.listCategoryId === LIST_CATEGORY_ID.POWER_SCORE_CONTACTS) {
      executeFrequentContactRequest(listName.key);
    } else {
      runSupplierListMemberRequest(listName.key, buyerCompanyKey);
    }
  };

  const runBidEventItemListRequest = React.useCallback(
    async (offeringKey: number) => {
      try {
        dispatch({ type: LIST_NAME_RESULT_SET_LOADING, isLoading: true });

        const bidEventItemsResponse = await axios.request(APIConfig.Sourcing.getBidEventItems(offeringKey));

        dispatch({
          type: LIST_NAME_RESULT_UPDATE,
          listNames: transformBidEventItemResultsToListNameItems(bidEventItemsResponse.data)
        });
      } catch (error) {
        executeSearchResultsReset();
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            'Error while loading bid event items',
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      } finally {
        dispatch({ type: LIST_NAME_RESULT_SET_LOADING, isLoading: false });
      }
    },
    [eventBusClient, executeSearchResultsReset]
  );

  const runSupplierListRequest = React.useCallback(
    async (listCategoryId: ListCategoryIdType) => {
      const listCategory = ListCategoryMap[listCategoryId];

      if (!isGlobalSupplierListCategoryItem(listCategory)) {
        return;
      }

      try {
        dispatch({ type: LIST_NAME_RESULT_SET_LOADING, isLoading: true });

        const supplierListResponse = await axios.request(
          APIConfig.SupplierList.getSupplierListsByCategory(
            listCategory.globalSupplierListCategory,
            listCategory.globalSupplierListType
          )
        );

        dispatch({
          type: LIST_NAME_RESULT_UPDATE,
          listNames: transformSupplierListResultsToListNameItems(supplierListResponse.data, listCategory.id)
        });
      } catch (error) {
        executeSearchResultsReset();
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            'Error while loading supplier lists',
            axios.isAxiosError(error) ? error.response : undefined
          )
        });
      } finally {
        dispatch({ type: LIST_NAME_RESULT_SET_LOADING, isLoading: false });
      }
    },
    [eventBusClient, executeSearchResultsReset]
  );

  const resetSearchResults = React.useCallback(() => {
    executeSearchResultsReset();
  }, [executeSearchResultsReset]);

  const runAllCompanySearch = React.useCallback(
    async (
      useNewSupplierSearch: boolean,
      page?: number,
      pageSize?: number
    ): Promise<{ resultCount: number; searchResults: ReadonlyArray<any> }> => {
      try {
        dispatch({ type: SEARCH_RESULTS_SET_LOADING, isLoading: true });
        const allCompanySearchResponse = await axios.request(
          APIConfig.Search.runAllCompanySearch({
            countryFilters: getCountryIdsFromCountryStateList(
              state.checkedFilterIdsMap[FILTER_TREE_TYPE.COUNTRY_OR_STATE]
            ),
            diversityClassificationFilters: generateDiverstiyClassificationFilters(
              state.checkedFilterIdsMap[FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION],
              state.filterTreeDataMap[FILTER_TREE_TYPE.DIVERSITY_CLASSIFICATION]
            ),
            diversityFilters: state.checkedFilterIdsMap[FILTER_TREE_TYPE.DIVERSE],
            invitedEventCountFilters: getSupplierActivityCountFiltersFromRange(
              state.invitedEventCountRangeStart,
              state.invitedEventCountRangeEnd
            ),
            invitedPortalCountFilters: getSupplierActivityCountFiltersFromRange(
              state.invitedPortalCountRangeStart,
              state.invitedPortalCountRangeEnd
            ),
            itemFilters: convertStringListToIntegerList(state.checkedFilterIdsMap[FILTER_TREE_TYPE.ITEM]),
            qualificationFilters: state.checkedFilterIdsMap[FILTER_TREE_TYPE.QUALIFICATION],
            segmentationFilters: state.checkedFilterIdsMap[FILTER_TREE_TYPE.SEGMENTATION],
            sourceFilters: state.checkedFilterIdsMap[FILTER_TREE_TYPE.SOURCE],
            stateFilters: getStateIdsFromCountryStateList(state.checkedFilterIdsMap[FILTER_TREE_TYPE.COUNTRY_OR_STATE]),
            page,
            pageSize,
            supplierIntelSchemaName: state.currentSupplierIntelSchemaName,
            useNewSupplierSearch
          })
        );

        dispatch({ type: SEARCH_RESULTS_SET_COMPANY_COUNT, companyCount: allCompanySearchResponse.data.totalRecords });

        return {
          resultCount: allCompanySearchResponse.data.totalRecords,
          searchResults: allCompanySearchResponse.data.content
        };
      } catch (error) {
        eventBusClient.sendEvent(EVENT.DISPLAY_MASKED_APP_DIALOG, {
          message: createRequestErrorMessage(
            'Error while running all company search',
            axios.isAxiosError(error) ? error.response : undefined
          )
        });

        return { resultCount: 0, searchResults: [] };
      } finally {
        dispatch({ type: SEARCH_RESULTS_SET_LOADING, isLoading: false });
      }
    },
    [
      eventBusClient,
      state.checkedFilterIdsMap,
      state.filterTreeDataMap,
      state.currentSupplierIntelSchemaName,
      state.invitedEventCountRangeEnd,
      state.invitedEventCountRangeStart,
      state.invitedPortalCountRangeEnd,
      state.invitedPortalCountRangeStart
    ]
  );

  const runBidEventItemListMemberRequest = (itemKey: string, buyerCompanyKey: number) => {
    executeListMemberRequestWithConfig(APIConfig.Sourcing.getBidEventItemSupplierList(itemKey), buyerCompanyKey);
  };

  const runSearch = React.useCallback(
    async (searchPhrase: string, searchOptions: SearchOptionsType = {}) => {
      await executeSearch(searchPhrase, searchOptions);
    },
    [executeSearch]
  );

  const runSupplierListMemberRequest = (listKey: string, buyerCompanyKey: number) => {
    executeListMemberRequestWithConfig(
      APIConfig.SupplierList.getSupplierListResults(parseInt(listKey, 10)),
      buyerCompanyKey
    );
  };

  const setSearchResultsCompanyCount = React.useCallback((companyCount: number) => {
    dispatch({ type: SEARCH_RESULTS_SET_COMPANY_COUNT, companyCount });
  }, []);

  const setCompanySearchEnabled = React.useCallback((isCompanySearchEnabled: boolean) => {
    dispatch({ type: SEARCH_RESULTS_SET_COMPANY_SEARCH_ENABLED, isCompanySearch: isCompanySearchEnabled });
  }, []);

  const setSearchResultsLoading = React.useCallback((isLoading: boolean) => {
    dispatch({ type: SEARCH_RESULTS_SET_LOADING, isLoading });
  }, []);

  const updateFilterOpenState = React.useCallback(
    (filterTreeType: FilterTreeType, filterId: string, isOpen: boolean) => {
      dispatch({ type: OPEN_FILTER_UPDATE, filterId, filterTreeType, isOpen });
    },
    []
  );

  const updateInvitedEventCountFilter = React.useCallback(
    (buyerCompanyKey: number, startingCount: number, endingCount: number, useNewSupplierSearch: boolean) => {
      // if set to default range, remove filter, otherwise add or update filter
      const isDefaultRange =
        startingCount === defaultState.invitedEventCountRangeStart &&
        endingCount === defaultState.invitedEventCountRangeEnd;

      const [treeNode] = state.invitedEventCountTreeData;

      if (!treeNode) {
        return;
      }

      // update users selected range values
      dispatch({ type: INVITED_EVENT_FILTER_RANGE_UPDATE, end: endingCount, start: startingCount });

      // update checked events invited filter ids
      const newCheckedInvitedEventCountFilterIds = isDefaultRange
        ? []
        : [SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID.INVITED_EVENT_COUNT];

      dispatch({
        type: CHECKED_FILTER_UPDATE,
        checkedFilterIds: newCheckedInvitedEventCountFilterIds,
        filterTreeType: FILTER_TREE_TYPE.INVITED_EVENT_COUNT
      });

      // update filter chips and search filters
      if (isDefaultRange) {
        // simulates un-checking invited event count filter tree node
        updateAppliedFilters(
          state.appliedFilterChips,
          newCheckedInvitedEventCountFilterIds,
          FILTER_TREE_TYPE.INVITED_EVENT_COUNT
        );
        dispatch({ type: SEARCH_FILTER_REMOVE, searchFilterName: SEARCH_FILTER_NAME.INVITED_EVENT_COUNT });
      } else {
        // simulates checking invited event count filter tree node

        // if applied filter chip already exists, update label, otherwise add a new one
        const newChipName = `Invited To ${getSummaryTextForSupplierActivityCountFilterRange(
          startingCount,
          endingCount
        )} Events`;

        const chipIndex = state.appliedFilterChips.findIndex(
          chip => chip.filterTreeType === FILTER_TREE_TYPE.INVITED_EVENT_COUNT
        );

        const updatedFilterChips =
          chipIndex !== -1
            ? state.appliedFilterChips.map(chip =>
                chip.id === SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID.INVITED_EVENT_COUNT
                  ? { ...chip, name: newChipName }
                  : chip
              )
            : [
                ...state.appliedFilterChips,
                getAppliedFilterChipForTreeNode(
                  { ...treeNode, name: newChipName },
                  { tooltip: supplierActivityFilterInvitedEventParentTreeNode.tooltip }
                )
              ];

        updateAppliedFilters(
          updatedFilterChips,
          newCheckedInvitedEventCountFilterIds,
          FILTER_TREE_TYPE.INVITED_EVENT_COUNT
        );
        dispatch({ type: SEARCH_FILTER_ADD, searchFilter: searchResultInvitedEventCountFilter });
      }

      // run search result update
      updateSearchResultsWithSupplierActivityFilters(
        'invitedEventCountFilters',
        buyerCompanyKey,
        startingCount,
        endingCount,
        useNewSupplierSearch
      );
    },
    [
      state.appliedFilterChips,
      state.invitedEventCountTreeData,
      updateAppliedFilters,
      updateSearchResultsWithSupplierActivityFilters
    ]
  );

  const updateInvitedPortalCountFilter = React.useCallback(
    (buyerCompanyKey: number, startingCount: number, endingCount: number, useNewSupplierSearch: boolean) => {
      // if set to default range, remove filter, otherwise add or update filter
      const isDefaultRange =
        startingCount === defaultState.invitedPortalCountRangeStart &&
        endingCount === defaultState.invitedPortalCountRangeEnd;

      const [treeNode] = state.invitedPortalCountTreeData;

      if (!treeNode) {
        return;
      }

      // update users selected range values
      dispatch({ type: INVITED_PORTAL_FILTER_RANGE_UPDATE, end: endingCount, start: startingCount });

      // update checked portals invited filter ids
      const newCheckedInvitedPortalCountFilterIds = isDefaultRange
        ? []
        : [SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID.INVITED_PORTAL_COUNT];

      dispatch({
        type: CHECKED_FILTER_UPDATE,
        checkedFilterIds: newCheckedInvitedPortalCountFilterIds,
        filterTreeType: FILTER_TREE_TYPE.INVITED_PORTAL_COUNT
      });

      // update filter chips and search filters
      if (isDefaultRange) {
        // simulates un-checking invited portal count filter tree node
        updateAppliedFilters(
          state.appliedFilterChips,
          newCheckedInvitedPortalCountFilterIds,
          FILTER_TREE_TYPE.INVITED_PORTAL_COUNT
        );
        dispatch({ type: SEARCH_FILTER_REMOVE, searchFilterName: SEARCH_FILTER_NAME.INVITED_PORTAL_COUNT });
      } else {
        // simulates checking invited portal count filter tree node

        // if applied filter chip already exists, update label, otherwise add a new one
        const newChipName = `Invited To ${getSummaryTextForSupplierActivityCountFilterRange(
          startingCount,
          endingCount
        )} Portals`;

        const chipIndex = state.appliedFilterChips.findIndex(
          chip => chip.filterTreeType === FILTER_TREE_TYPE.INVITED_PORTAL_COUNT
        );

        const updatedFilterChips =
          chipIndex !== -1
            ? state.appliedFilterChips.map(chip =>
                chip.id === SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID.INVITED_PORTAL_COUNT
                  ? { ...chip, name: newChipName }
                  : chip
              )
            : [
                ...state.appliedFilterChips,
                getAppliedFilterChipForTreeNode(
                  { ...treeNode, name: newChipName },
                  { tooltip: supplierActivityFilterInvitedPortalParentTreeNode.tooltip }
                )
              ];

        updateAppliedFilters(
          updatedFilterChips,
          newCheckedInvitedPortalCountFilterIds,
          FILTER_TREE_TYPE.INVITED_PORTAL_COUNT
        );
        dispatch({ type: SEARCH_FILTER_ADD, searchFilter: searchResultInvitedPortalCountFilter });
      }

      // run search result update
      updateSearchResultsWithSupplierActivityFilters(
        'invitedPortalCountFilters',
        buyerCompanyKey,
        startingCount,
        endingCount,
        useNewSupplierSearch
      );
    },
    [
      state.appliedFilterChips,
      state.invitedPortalCountTreeData,
      updateAppliedFilters,
      updateSearchResultsWithSupplierActivityFilters
    ]
  );

  const updateListCategory = React.useCallback(
    (listCategoryId: ListCategoryIdType) => {
      dispatch({ type: LIST_CATEGORY_CHANGE, listCategoryId });
      executeSearchResultsReset();

      if (listCategoryId === LIST_CATEGORY_ID.BID_EVENT_ITEMS_LIST) {
        runBidEventItemListRequest(offeringKey || 0);
      } else if (listCategoryId === LIST_CATEGORY_ID.POWER_SCORE_CONTACTS) {
        // do not show any lists names when power score category selected
        dispatch({ type: LIST_NAME_RESULT_RESET });
      } else {
        runSupplierListRequest(listCategoryId);
      }
    },
    [executeSearchResultsReset, offeringKey, runBidEventItemListRequest, runSupplierListRequest]
  );

  const updateSearchPhraseInputValue = (searchPhraseInputValue: string) => {
    dispatch({ type: SEARCH_PHRASE_INPUT_VALUE_UPDATE, searchPhraseInputValue });
  };

  const updateSearchMode = React.useCallback(
    (searchMode: SearchModeType, phrase?: string) => {
      dispatch({ type: SEARCH_MODE_CHANGE, searchMode });
      executeSearchResultsReset();

      if (
        searchMode === SEARCH_MODE.COMPANY_LISTS &&
        state.selectedListCategoryId !== LIST_CATEGORY_ID.BID_EVENT_ITEMS_LIST
      ) {
        runSupplierListRequest(state.selectedListCategoryId);
      } else if (isDiscovery) {
        runSearch(phrase || '', {
          buyerCompanyKey: state.currentSearchBuyerCompanyKey,
          viewMode: state.viewMode,
          useNewSupplierSearch: searchMode === SEARCH_MODE.IPAFB_SUPPLIER_SEARCH
        });
      }
    },
    [
      executeSearchResultsReset,
      runSupplierListRequest,
      runSearch,
      state.selectedListCategoryId,
      state.currentSearchBuyerCompanyKey,
      state.viewMode,
      isDiscovery
    ]
  );

  const updateSearchResultsAlphabeticalOrderEnabled = React.useCallback(
    (isAlphabeticalOrderEnabled: boolean) => {
      dispatch({ type: SEARCH_RESULTS_SET_ALPHABETICAL_ORDER_ENABLED, isEnabled: isAlphabeticalOrderEnabled });
      dispatch({
        type: SEARCH_RESULTS_UPDATE,
        searchResults: isAlphabeticalOrderEnabled
          ? getSearchResultsSortedAlphabetically(state.currentSearchResults)
          : getSearchResultsSortedByRelevancyScore(state.currentSearchResults)
      });
    },
    [state.currentSearchResults]
  );

  const updateSearchResultsViewMode = React.useCallback(
    (viewMode: SearchResultViewModeType) => {
      //we are not forcing a termSearch anymore, ES is taking care of handling empty string
      dispatch({ type: SEARCH_RESULTS_SET_VIEW_MODE, viewMode });
      runSearch(state.searchPhraseForLastSearch || '', {
        buyerCompanyKey: state.currentSearchBuyerCompanyKey,
        viewMode: viewMode
      });
    },
    [state.searchPhraseForLastSearch, state.currentSearchBuyerCompanyKey, runSearch]
  );

  const getSearchContextState = () => {
    return {
      ...state
    };
  };
  // generate context value
  const contextValue: SearchContextValue = [
    // first element, search data
    { ...state },
    // second element, search action functions
    {
      addCheckedFilter,
      addSearchFilter,
      addSupplierParentCompanyFilterExclusion,
      removeCheckedFilter,
      removeSearchFilter,
      resetSearchFilters,
      resetSearchResults,
      runAllCompanySearch,
      runBidEventItemListRequest,
      runListMemberRequest,
      runSearch,
      runSupplierListRequest,
      setCompanySearchEnabled,
      setSearchResultsCompanyCount,
      setSearchResultsLoading,
      updateFilterOpenState,
      updateInvitedEventCountFilter,
      updateInvitedPortalCountFilter,
      updateListCategory,
      updateSearchPhraseInputValue,
      updateSearchMode,
      updateSearchResultsAlphabeticalOrderEnabled,
      updateSearchResultsViewMode,
      getSearchContextState
    }
  ];

  return <SearchContext.Provider value={contextValue}>{children}</SearchContext.Provider>;
};

//
// --- useSearchContextHook ---

/**
 * useSearchContext hook
 *
 * external hook that returns product data and action handlers based on the current product
 * type app is being used on.
 *
 * The return result is an array where the the first element is a data object. The second element
 * is a map of actions.
 *
 * Example Use 1:
 *   const [searchData, searchAction] = useSearchContext();
 *
 * Example Use 2:
 *   const [{ searchMode }, { updateSearchMode }] = useSearchContext();
 */
export const useSearchContext = (): SearchContextValue => {
  const context = React.useContext(SearchContext);

  if (!context) {
    throw new Error('useSearchContext must be used within a SearchContextProvider');
  }

  return context;
};
